import { createBrowserRouter, Outlet, useNavigate, type RouteObject } from 'react-router';
import { AppShell, TeamOnly } from '@redbax/ui-kit';
import { IdentityGate } from '@redbax/shared';
import { dashboardRoutes } from '@redbax/ui-dashboard';
import { eventsRoutes } from '@redbax/ui-events';
import { policiesRoutes } from '@redbax/ui-policies';
import { aiServicesRoutes } from '@redbax/ui-ai-services';
import { devicesRoutes } from '@redbax/ui-devices';
import { packageSecurityRoutes } from '@redbax/ui-package-security';
import { settingsRoutes } from '@redbax/ui-settings';
import { onboardingRoutes } from '@redbax/ui-onboarding';

function IdentityGateLayout() {
  const navigate = useNavigate();
  return (
    <IdentityGate
      onNeedsOnboarding={() => navigate('/onboarding', { replace: true })}
      onNeedsApproval={(orgName, orgId) => navigate('/awaiting-approval', { replace: true, state: { orgName, orgId } })}
    >
      <Outlet />
    </IdentityGate>
  );
}

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * Wraps a feature package's routes so a free personal organization gets an upgrade panel where the
 * feature would be (epic #121, story #134).
 *
 * The routes stay REGISTERED rather than being left out of the tree for a free user. A route that does
 * not exist is a 404 on a bookmark or a link someone shared, which reads as a broken product instead of
 * a feature they have not bought - and this way the page can name the feature and offer it.
 *
 * Presentation only. PersonalOrgConfigurationFilter is what actually refuses the writes, because
 * anybody can call the API without going through this app at all.
 */
function teamOnly(routes: RouteObject[], feature: string): RouteObject[] {
  return routes.map((route) => ({
    ...route,
    element: <TeamOnly feature={feature}>{route.element}</TeamOnly>,
  }));
}

export const router = createBrowserRouter([
  ...onboardingRoutes,
  {
    element: <IdentityGateLayout />,
    children: [
      {
        path: '/',
        element: <ShellLayout />,
        children: [
          ...dashboardRoutes,
          ...eventsRoutes,
          ...teamOnly(policiesRoutes, 'Policies'),
          ...teamOnly(aiServicesRoutes, 'AI Services'),
          ...devicesRoutes,
          ...teamOnly(packageSecurityRoutes, 'Package Security'),
          ...settingsRoutes,
        ],
      },
    ],
  },
]);
